/**
 * Image Optimization Utilities
 * ✅ PERFORMANCE: Tools for optimizing images and reducing bundle size
 */
/**
 * Get optimized image URL with size parameters
 * Helps reduce large image file sizes
 */
export function getOptimizedImageUrl(originalUrl, options = {}) {
    // For now, return original URL
    // In production, this could integrate with image optimization services
    // like Cloudinary, ImageKit, or custom image processing
    if (!originalUrl)
        return '';
    // If it's a local asset, return as-is
    if (originalUrl.startsWith('/') || originalUrl.startsWith('./')) {
        return originalUrl;
    }
    // For external URLs, could add query parameters for services that support them
    const url = new URL(originalUrl);
    if (options.width) {
        url.searchParams.set('w', options.width.toString());
    }
    if (options.height) {
        url.searchParams.set('h', options.height.toString());
    }
    if (options.quality) {
        url.searchParams.set('q', options.quality.toString());
    }
    if (options.format) {
        url.searchParams.set('f', options.format);
    }
    return url.toString();
}
/**
 * Create responsive image srcset for different screen sizes
 */
export function createResponsiveImageSrcSet(baseUrl) {
    const sizes = [480, 768, 1024, 1280, 1920];
    return sizes
        .map(size => `${getOptimizedImageUrl(baseUrl, { width: size })} ${size}w`)
        .join(', ');
}
/**
 * Get image file size category for optimization warnings
 */
export function getImageSizeCategory(sizeInBytes) {
    const sizeInKB = sizeInBytes / 1024;
    if (sizeInKB < 50)
        return 'small';
    if (sizeInKB < 200)
        return 'medium';
    if (sizeInKB < 500)
        return 'large';
    return 'xlarge';
}
/**
 * Development helper to log large images
 */
export function logLargeImages() {
    if (process.env.NODE_ENV === 'development') {
        // This could be used to identify large assets during development
        console.log('🖼️ Image optimization: Use getOptimizedImageUrl() for large images');
    }
}
